import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import FamlaServices from "../../../services/api/FamlaServices";
import { useAppSelector } from "../../../stores/store";

type CampaignProgress = {
    objectives_defined: boolean;
    collaborators_invited: boolean;
    interviews_in_progress: boolean;
    interviews_completed: boolean;
    mappings_being_finalized: boolean;
    report_available: boolean;
}

function Objective({achieved, text, isCurrentStep}: {achieved: boolean, text: string, isCurrentStep: boolean}){
    return <div className="d-flex align-items-center gap-2">
        {isCurrentStep ? (
            <i className="bi bi-circle-fill text-success"></i>
        ) : (
            achieved ? (
                <i className="bi bi-check-circle-fill text-success"></i>
            ) : (
                <i className="bi bi-circle text-secondary"></i>
            )
        )}
        <span className={`small ${achieved ? 'line-through text-gray-400' : 'text-light'}`}>
            {text}
        </span>
    </div>
}

export default function CampaignStatus({campaignId}: {campaignId: string}) {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const [campaignStatus, setCampaignStatus] = useState<CampaignProgress | undefined>(undefined);
    const [validatedStep, setValidatedStep] = useState<number>(-1);

    useEffect(() => {
        const fetchCampaignStatus = async () => {
            try {
                const response = await FamlaServices(
                    "api_host",
                    `/campaign/${campaignId}/progress`,
                    "GET",
                    "",
                    users.access_token
                )
                setCampaignStatus(response.body.data);

                let countvalidatedStep = 0
                
                Object.values(response.body.data).forEach((isStepCompleted) => {
                    if (isStepCompleted) {
                        countvalidatedStep++;
                    }
                });

                setValidatedStep(countvalidatedStep);
            } catch (error) {
                alert(error);
                return <div>Error fetching campaign progress</div>;
            }
        }

        fetchCampaignStatus();
    }, [campaignId]);

    if (!campaignStatus) {
        return <div className="text-light">Loading ...</div>;
    }

    return (
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <div className="card-body">
                <div className="mb-3">
                    <h6 className="card-title mb-0 fw-bold text-light">
                        {t('campaigns.todoList')}
                    </h6>
                </div>
                <div className="d-flex flex-column gap-2">
                    <Objective 
                        achieved={campaignStatus.objectives_defined} 
                        text={t('campaigns.todoDefinitionObjectives')}
                        isCurrentStep={validatedStep === 0}
                    />
                    <Objective 
                        achieved={campaignStatus.collaborators_invited} 
                        text={t('campaigns.todoInviteCollaborator')} 
                        isCurrentStep={validatedStep === 1}
                    />
                    <Objective 
                        achieved={campaignStatus.interviews_in_progress} 
                        text={t('campaigns.todoInterviewsInProgress')} 
                        isCurrentStep={validatedStep === 2}
                    />
                    <Objective 
                        achieved={campaignStatus.interviews_completed} 
                        text={t('campaigns.todoInterviewsCompleted')} 
                        isCurrentStep={validatedStep === 3}
                    />
                    <Objective 
                        achieved={campaignStatus.mappings_being_finalized} 
                        text={t('campaigns.todoMappingsInProgress')} 
                        isCurrentStep={validatedStep === 4}
                    />
                    <Objective 
                        achieved={campaignStatus.report_available} 
                        text={t('campaigns.todoReportAvailable')} 
                        isCurrentStep={validatedStep === 5}
                    />
                </div>
            </div>
        </div>
    )
}